import React from 'react';
import { STATUS_PILL, formatPhone } from './powerDialConstants.js';

const CALLER_ID_BADGES = {
  local: { text: '🎯 Local (Area Code)', label: 'Local Area Code Match', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  state: { text: '📍 In-State', label: 'In-State Match', cls: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  neighbor: { text: '🗺️ Neighbor State', label: 'Neighboring State Match', cls: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  default: { text: '📞 Default Line', label: 'Default Line', cls: 'bg-white/10 text-white/70 border-white/15' },
};

export default function RunBarMemberChip({
  member,
  session,
  leadInfo = {},
  onSelect,
}) {
  const m = member;
  const pill = STATUS_PILL[m.status] || { label: m.status, cls: 'bg-white/10 text-white/60' };
  const name = leadInfo.firstName || leadInfo.lastName
    ? `${leadInfo.firstName || ''} ${leadInfo.lastName || ''}`.trim()
    : 'Lead';
  const isPaused = session?.status === 'paused';
  const isConnected = m.status === 'connected' || (session.connectedCallSid && m.callSid === session.connectedCallSid);
  const isRinging = (m.status === 'ringing' || m.status === 'dialing') && !isPaused;
  const isCancelled = m.status === 'cancelled' || m.status === 'failed';
  const attemptNum = m.attempts || 1;
  const maxAttempts = session?.redialAttempts || 3;
  const callerIdNum = m.fromNumber || session.outboundCallerId;
  const callerIdMatch = m.callerIdMatch || (session.smartLocalId !== false && m.fromNumber ? 'local' : 'default');

  return (
    <div
      onClick={onSelect}
      title={`Click to view profile · Attempt ${attemptNum} of ${maxAttempts}`}
      className={`min-w-0 px-3 py-1.5 rounded-xl text-xs flex flex-col justify-center border cursor-pointer hover:border-civix-purple/60 ${
        isConnected
          ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-100 shadow-[0_0_15px_rgba(16,185,129,0.3)] ring-1 ring-emerald-400/50'
          : isRinging
          ? 'bg-blue-500/15 border-blue-500/35 text-blue-200'
          : isCancelled
          ? 'bg-white/5 border-white/5 text-white/40 opacity-60'
          : isPaused
          ? 'bg-white/5 border-white/10 text-white/60'
          : pill.cls
      }`}
    >
      <div className="flex items-center justify-between gap-1 w-full">
        <span className="font-semibold truncate text-[11px]" title={name}>{name}</span>
        {isConnected && !isPaused && (
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
        )}
        {isRinging && (
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-pulse relative inline-flex rounded-full h-2 w-2 bg-blue-400"></span>
          </span>
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] mt-0.5">
        <span className="opacity-80 truncate">
          {isPaused ? 'Paused'
            : isRinging ? `Attempt ${attemptNum}/${maxAttempts} - Ringing...`
            : `${pill.label} · Attempt ${attemptNum}/${maxAttempts}`}
        </span>
        {leadInfo.phone && (
          <span className="text-white/40 text-[9px] font-mono truncate">{formatPhone(leadInfo.phone)}</span>
        )}
      </div>
      {/* Outbound Caller ID Display */}
      <div
        className="text-[9px] text-white/60 truncate mt-1 pt-1 border-t border-white/5 flex items-center justify-between gap-1"
        title={`Calling from: ${callerIdNum ? formatPhone(callerIdNum) : 'Resolving…'} [${(CALLER_ID_BADGES[callerIdMatch] || CALLER_ID_BADGES.default).label}]`}
      >
        <span className="truncate">
          <span className="text-white/40">Calling from: </span>
          <span className="font-mono text-white/80">
            {callerIdNum ? formatPhone(callerIdNum) : 'Resolving…'}
          </span>
        </span>
        <span className={`text-[8px] px-1 py-0.2 rounded font-semibold shrink-0 border ${(CALLER_ID_BADGES[callerIdMatch] || CALLER_ID_BADGES.default).cls}`}>
          {(CALLER_ID_BADGES[callerIdMatch] || CALLER_ID_BADGES.default).text}
        </span>
      </div>
    </div>
  );
}
